import axios from 'axios';
import { supabase, getAccessToken, waitForAuthReady } from './supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Check if the current user is a demo user (no real Supabase session)
const isDemoUser = () => {
  try {
    const stored = localStorage.getItem('draftflow_user');
    return stored ? JSON.parse(stored).isDemo === true : false;
  } catch { return false; }
};

// Inject Supabase JWT into every request via Authorization header.
// Uses supabase.auth.getSession() to get the freshest token (auto-refreshed).
// Unauthenticated routes (e.g., POST /profiles for signup) will still work —
// the backend simply won't find a valid JWT and the route must be unprotected.
// Demo users skip JWT injection entirely — they have no Supabase session.
api.interceptors.request.use(async (config) => {
  if (isDemoUser()) {
    const isProfileCall = config.url?.includes('/profiles');
    if (isProfileCall) return config;

    const demoEnabled = process.env.REACT_APP_DEMO_MODE_ENABLED !== 'false';
    if (!demoEnabled) {
      return Promise.reject(Object.assign(new Error('Demo mode — skipping API call'), {
        response: { status: 401 }, isDemo: true, config
      }));
    }

    const { handleDemoRequest } = await import('./demo');
    config.adapter = () => handleDemoRequest(config);
    return config;
  }

  // Read the cached token synchronously (after a one-time readiness await).
  // NEVER call supabase.auth.getSession() here — per-request getSession calls
  // race for the auth-token Web Lock and steal it from each other, which was
  // the root cause of the Checkpoint D "spins forever" failures (Q12).
  await waitForAuthReady();
  const token = getAccessToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses — session expired or invalid
// Don't redirect during auth callback to avoid login loops
// Don't redirect demo users — they'll get 401s on every call, which is expected
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      if (isDemoUser()) return Promise.reject(error);

      const isAuthCallback = window.location.pathname === '/auth/callback' ||
        window.location.hash.includes('access_token');
      if (!isAuthCallback) {
        // Try refreshing the session once before giving up
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          localStorage.removeItem('draftflow_user');
          // Human-readable explanation shown on the landing page banner
          sessionStorage.setItem('draftflow_auth_error', 'Your session expired. Please sign in again to pick up where you left off.');
          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

// ============== Profile APIs ==============
export const createProfile = async (profileData) => {
  const response = await api.post('/profiles', profileData);
  return response.data;
};

export const getProfile = async (profileId) => {
  const response = await api.get(`/profiles/${profileId}`);
  return response.data;
};

export const getProfileByEmail = async (email) => {
  const response = await api.get(`/profiles/email/${email}`);
  return response.data;
};

export const updateProfile = async (profileId, updates) => {
  const response = await api.patch(`/profiles/${profileId}`, updates);
  return response.data;
};

// ============== Assignment APIs ==============
export const createAssignment = async (assignmentData) => {
  const response = await api.post('/assignments', assignmentData);
  return response.data;
};

export const getAssignments = async (teacherId = null, status = 'active') => {
  const params = new URLSearchParams({ status });
  if (teacherId) params.append('teacher_id', teacherId);
  const response = await api.get(`/assignments?${params}`);
  return response.data;
};

export const getAssignment = async (assignmentId) => {
  const response = await api.get(`/assignments/${assignmentId}`);
  return response.data;
};

export const updateAssignment = async (assignmentId, updates) => {
  const response = await api.patch(`/assignments/${assignmentId}`, updates);
  return response.data;
};

// Public, minimal preview for the pre-auth assignment join page
export const getAssignmentPreview = async (assignmentId) => {
  const response = await api.get(`/assignments/${assignmentId}/preview`);
  return response.data;
};

// Single round-trip entry: auto-enroll + find-or-create draft → {assignment, draft}
export const enterAssignment = async (assignmentId, joinCode = null) => {
  const response = await api.post(
    `/assignments/${assignmentId}/entry`,
    joinCode ? { join_code: joinCode } : {}
  );
  return response.data;
};

// Coach feed-up greeting (generated once per draft, cached server-side)
export const getFeedupGreeting = async (draftId) => {
  const response = await api.post(`/feedback/feedup/${draftId}`);
  return response.data;
};

// ============== Draft APIs ==============
export const createDraft = async (assignmentId) => {
  const response = await api.post(`/drafts?assignment_id=${assignmentId}`);
  return response.data;
};

export const getDrafts = async (studentId = null, assignmentId = null, scope = null) => {
  const params = new URLSearchParams();
  if (studentId) params.append('student_id', studentId);
  if (assignmentId) params.append('assignment_id', assignmentId);
  if (scope) params.append('scope', scope);
  const response = await api.get(`/drafts?${params}`);
  return response.data;
};

export const getDraft = async (draftId) => {
  const response = await api.get(`/drafts/${draftId}`);
  return response.data;
};

export const updateDraft = async (draftId, content, wordCount = null) => {
  const response = await api.patch(`/drafts/${draftId}`, {
    content,
    word_count: wordCount
  });
  return response.data;
};

export const submitDraft = async (draftId) => {
  const response = await api.post(`/drafts/${draftId}/submit`);
  return response.data;
};

export const submitTeacherFeedback = async (draftId, feedback) => {
  const response = await api.patch(`/drafts/${draftId}/teacher-feedback`, { feedback });
  return response.data;
};

export const updateDraftStage = async (draftId, stage, force = false) => {
  const response = await api.patch(`/drafts/${draftId}/stage`, { stage, force });
  return response.data;
};

export const returnForRevision = async (draftId) => {
  const response = await api.post(`/drafts/${draftId}/return-for-revision`);
  return response.data;
};

// ============== DraftTrack APIs ==============
export const logTrackEvent = async (draftId, eventType, contentDiff, wordCount) => {
  const params = new URLSearchParams({
    draft_id: draftId,
    event_type: eventType,
    content_diff: contentDiff,
    word_count: wordCount.toString()
  });
  const response = await api.post(`/draft-track?${params}`);
  return response.data;
};

export const getTrackEvents = async (draftId) => {
  const response = await api.get(`/draft-track/${draftId}`);
  return response.data;
};

// ============== AI Feedback APIs ==============
export const getAIFeedback = async (draftContent, assignmentPrompt, ccssStandards = [], feedbackType = 'comprehensive', writingStage = '', draftId = '') => {
  const response = await api.post('/feedback/ai', {
    draft_content: draftContent,
    assignment_prompt: assignmentPrompt,
    ccss_standards: ccssStandards,
    feedback_type: feedbackType,
    writing_stage: writingStage,
    // FlowGrade: with a draft_id the backend persists server-side and applies
    // the assignment's review mode (may return no text, review_status pending).
    draft_id: draftId,
  });
  return response.data;
};

// ============== FlowGrade (teacher review queue) ==============
export const getPendingFeedback = async (assignmentId) => {
  const response = await api.get(`/feedback/pending?assignment_id=${assignmentId}`);
  return response.data;
};

export const approveFeedback = async (feedbackId) => {
  const response = await api.post(`/feedback/${feedbackId}/approve`);
  return response.data;
};

export const editFeedback = async (feedbackId, feedbackText, reviewNote = '') => {
  const response = await api.patch(`/feedback/${feedbackId}/edit`, {
    feedback_text: feedbackText,
    review_note: reviewNote,
  });
  return response.data;
};

export const rejectFeedback = async (feedbackId, regenerate = false) => {
  const response = await api.post(`/feedback/${feedbackId}/reject`, { regenerate });
  return response.data;
};

export const releaseFeedbackBatch = async ({ assignmentId = '', ids = [] } = {}) => {
  const response = await api.post('/feedback/release-batch', {
    assignment_id: assignmentId,
    ids,
  });
  return response.data;
};

export const logFeedback = async (feedbackData) => {
  const response = await api.post('/feedback/log', feedbackData);
  return response.data;
};

export const getStudentFeedback = async (studentId) => {
  const response = await api.get(`/feedback/student/${studentId}`);
  return response.data;
};

// ============== Rubric APIs ==============
export const generateRubric = async (ccssStandard, assignmentContext = null) => {
  const response = await api.post('/rubric/generate', {
    ccss_standard: ccssStandard,
    assignment_context: assignmentContext
  });
  return response.data;
};

// ============== Rubric CRUD APIs ==============
export const createRubric = async (rubricData) => {
  const response = await api.post('/rubrics', rubricData);
  return response.data;
};

export const getRubrics = async (assignmentId = null) => {
  const params = assignmentId ? `?assignment_id=${assignmentId}` : '';
  const response = await api.get(`/rubrics${params}`);
  return response.data;
};

export const getRubric = async (rubricId) => {
  const response = await api.get(`/rubrics/${rubricId}`);
  return response.data;
};

// Coach transcript for a draft (Q10) — teacher read-only, RLS-scoped.
export const getCoachMessages = async (draftId) => {
  const response = await api.get(`/drafts/${draftId}/coach-messages`);
  return response.data;
};

export const updateRubric = async (rubricId, updates) => {
  const response = await api.patch(`/rubrics/${rubricId}`, updates);
  return response.data;
};

export const deleteRubric = async (rubricId) => {
  const response = await api.delete(`/rubrics/${rubricId}`);
  return response.data;
};

// ============== Drafty Chat APIs ==============
export const chatWithDrafty = async (messages, draftContent = null, draftId = null) => {
  const response = await api.post('/chat/drafty', {
    messages,
    draft_content: draftContent,
    draft_id: draftId
  });
  return response.data;
};

// ============== Accommodations APIs ==============
export const getAccommodations = async (studentId) => {
  const response = await api.get(`/accommodations/${studentId}`);
  return response.data;
};

export const setAccommodations = async (accommodationData) => {
  const response = await api.post('/accommodations', accommodationData);
  return response.data;
};

// ============== Peer Review APIs ==============
// ============== Analytics APIs ==============
export const getClassAnalytics = async (teacherId) => {
  const response = await api.get(`/analytics/class/${teacherId}`);
  return response.data;
};

export const getStudentAnalytics = async (studentId) => {
  const response = await api.get(`/analytics/student/${studentId}`);
  return response.data;
};

// ============== Timer APIs ==============
// ============== Guard APIs ==============
export const getContentAlerts = async (teacherId) => {
  const response = await api.get(`/guard/alerts/${teacherId}`);
  return response.data;
};

export const analyzeGuardDraft = async (draftId) => {
  const response = await api.post(`/guard/analyze/${draftId}`);
  return response.data;
};

export const getDraftGuardAlerts = async (draftId) => {
  const response = await api.get(`/guard/alerts/draft/${draftId}`);
  return response.data;
};

// ============== Google Drive Export ==============

export const exportToDrive = async (draftId, folderId = null) => {
  const params = folderId ? `?folder_id=${folderId}` : '';
  const response = await api.post(`/drive/export/${draftId}${params}`);
  return response.data;
};

// ============== Class Management APIs ==============
export const createClass = async (className = 'My Class') => {
  const response = await api.post('/classes', { class_name: className });
  return response.data;
};

export const getMyClasses = async () => {
  const response = await api.get('/classes');
  return response.data;
};

export const joinClass = async (joinCode) => {
  const response = await api.post('/classes/join', { join_code: joinCode });
  return response.data;
};

export const getClassMembers = async (joinCode) => {
  const response = await api.get(`/classes/${joinCode}/members`);
  return response.data;
};

export const getClassAssignments = async (joinCode) => {
  const response = await api.get(`/classes/${joinCode}/assignments`);
  return response.data;
};

// ============== Socratic Chat APIs ==============
// ============== Research Tools APIs ==============
// ============== Growth & Reflection APIs ==============
// ============== Sentence Starters APIs ==============
export const getSentenceStarters = async (assignmentPrompt, ccssStandards = [], stage = 'draft', writingType = 'argumentative') => {
  const response = await api.post('/ai/sentence-starters', {
    assignment_prompt: assignmentPrompt,
    ccss_standards: ccssStandards,
    stage,
    writing_type: writingType,
  });
  return response.data;
};

// ============== Progress Analytics APIs ==============
export const getAssignmentProgress = async (assignmentId) => {
  const response = await api.get(`/analytics/assignment/${assignmentId}/progress`);
  return response.data;
};

// One student's trajectory across assignments (Insights, Phase 6 item 4).
export const getStudentProgress = async (studentId) => {
  const response = await api.get(`/analytics/student/${studentId}/progress`);
  return response.data;
};

// Process evidence for one draft (Phase 6 item 5 / R8) — teacher-only.
export const getAuthenticityReport = async (draftId) => {
  const response = await api.get(`/analytics/draft/${draftId}/authenticity`);
  return response.data;
};

// ============== Portfolio APIs ==============
// ============== DraftVision APIs ==============
// ============== Google Drive APIs ==============
export const getDriveStatus = async () => {
  const response = await api.get('/drive/status');
  return response.data;
};

export const getDriveAuthUrl = async () => {
  const response = await api.get('/drive/auth-url');
  return response.data;
};

export const getDriveFolders = async () => {
  const response = await api.get('/drive/folders');
  return response.data;
};

// ============== Google Classroom APIs ==============
export const getGCAuthUrl = async () => {
  const response = await api.get('/gc/auth-url');
  return response.data;
};

export const getGCStatus = async () => {
  const response = await api.get('/gc/status');
  return response.data;
};

export const getGCCourses = async () => {
  const response = await api.get('/gc/courses');
  return response.data;
};

export const getGCCourseStudents = async (courseId) => {
  const response = await api.get(`/gc/courses/${courseId}/students`);
  return response.data;
};

export const importGCCourse = async (courseId) => {
  const response = await api.post(`/gc/import/${courseId}`);
  return response.data;
};

export default api;
